import { match } from 'ts-pattern'
import {
  type AnalysisStreamEvent,
  type FindingProgressContent,
  type WidgetSnapshotContent,
} from '../../utils/api-client/sse-schemas.ts'
import { type ActivityStepCardProps } from './activity-step-card.tsx'
import { type ActivityWidgetItem } from './activity-widget.tsx'

/** A widget folded to its freshest snapshot. `sequence` is retained so a stale re-send can be ignored. */
type FoldedWidget = ActivityWidgetItem & { sequence: number }

/**
 * One folded step. The card owns the rendered shape, so this derives from its props rather than restating
 * them — `activity-feed.tsx` spreads a step straight into `ActivityStepCard`, and a JSX spread gets no
 * excess-property check, so two independent declarations would drift in either direction without a type
 * error. `activityId` is the one field the fold needs and the card does not render.
 */
export type ActivityStep = ActivityStepCardProps & {
  /**
   * The producer-supplied `activity_id`, when the emitter sends one. Stable across every emission for this
   * activity, which is what lets successive events redraw one card instead of stacking several.
   */
  activityId?: string
}

type FoldState = {
  /** Render order, which is first-seen insertion order. */
  steps: ActivityStep[]
  stepsByActivityId: Map<string, ActivityStep>
  /** The pre-`activity_id` fallback index, kept for events an un-upgraded emitter sends (ISS-8779). */
  stepsByTitle: Map<string, ActivityStep>
  /**
   * Container `activity_id` → widget `activity_id` → freshest snapshot. Keyed by container rather than
   * hung off the step, which buys two properties for free: a snapshot naming a container the client has
   * not seen is held here rendering nothing until that container arrives, and a container that re-emits
   * cannot orphan the widgets nested inside it.
   */
  widgetsByContainerId: Map<string, Map<string, FoldedWidget>>
}

function findStep({ stepsByActivityId, stepsByTitle }: FoldState, event: FindingProgressContent) {
  return event.activity_id ? stepsByActivityId.get(event.activity_id) : stepsByTitle.get(event.title)
}

/**
 * Upsert an in-progress step. An activity that already has a step updates it in place (reopening it to
 * in_progress) rather than appending a duplicate card, so activities that re-emit progress — e.g. after an
 * SSE reconnect replays the whole event log from offset 0 — collapse onto their existing step.
 */
function applyInProgressEvent(state: FoldState, event: FindingProgressContent): void {
  const existingStep = findStep(state, event)
  if (existingStep) {
    existingStep.description = event.description
    existingStep.status = 'in_progress'
    return
  }

  const step: ActivityStep = {
    title: event.title,
    description: event.description,
    status: 'in_progress',
    activityId: event.activity_id,
  }
  state.steps.push(step)
  if (event.activity_id) {
    state.stepsByActivityId.set(event.activity_id, step)
  } else {
    state.stepsByTitle.set(event.title, step)
  }
}

/**
 * Close the step the completion belongs to, letting emitters surface richer result text (e.g. "Retrieved 3
 * feedback entries") after a generic in-progress prompt (e.g. "Searching prior feedback").
 *
 * With an `activity_id` the completion is attributable, so only that activity's step closes. Without one we
 * keep the legacy sweep over every in-progress step: a pre-ISS-8200 emitter gives no way to tell which
 * activity actually finished, and dropping the sweep would leave those steps spinning forever.
 */
function applyCompletedEvent(state: FoldState, completedEvent: FindingProgressContent): void {
  if (completedEvent.activity_id) {
    const step = state.stepsByActivityId.get(completedEvent.activity_id)
    if (step) {
      step.status = 'completed'
      step.description = completedEvent.description
    }
    return
  }

  for (const step of state.steps) {
    if (step.status !== 'in_progress') {
      continue
    }
    step.status = 'completed'
    if (step.title === completedEvent.title) {
      step.description = completedEvent.description
    }
  }
}

/** Give a widget that names no container a top-level step of its own to render inside. */
function ensureTopLevelStep(state: FoldState, activityId: string): void {
  if (state.stepsByActivityId.has(activityId)) {
    return
  }

  const step: ActivityStep = { status: 'in_progress', activityId }
  state.steps.push(step)
  state.stepsByActivityId.set(activityId, step)
}

/**
 * Fold one widget snapshot into the freshest state for its `activity_id`. Every snapshot is the widget's
 * complete current state rather than a delta, so keeping the highest `activity_sequence` is all the fold
 * needs — and the store this reads from never de-duplicates, orders strictly by receipt, and replays
 * everything on each connect.
 */
function applyWidgetSnapshot(state: FoldState, content: WidgetSnapshotContent): void {
  const { activity_id: activityId, container_id: containerId } = content
  // With no identity there is no fold key, so this snapshot can neither supersede an earlier one nor be
  // superseded. Drop it rather than stack a fresh card per emission.
  if (!activityId) {
    return
  }

  // A widget naming no container is itself top level, so it nests under its own activity.
  if (!containerId) {
    ensureTopLevelStep(state, activityId)
  }

  const targetContainerId = containerId ?? activityId
  const widgets = state.widgetsByContainerId.get(targetContainerId) ?? new Map<string, FoldedWidget>()
  state.widgetsByContainerId.set(targetContainerId, widgets)

  const sequence = content.activity_sequence ?? 0
  const foldedWidget = widgets.get(activityId)
  // A repeated or stale sequence is a no-op. That is what makes replay from offset 0 idempotent and an
  // out-of-order live tail harmless.
  if (foldedWidget && sequence <= foldedWidget.sequence) {
    return
  }

  widgets.set(activityId, { id: activityId, sequence, payload: content.payload })
}

/**
 * Hand each step the widgets nested under it. Widgets whose container never appeared in the stream stay
 * behind in `widgetsByContainerId` and render nothing — not dropped, since a snapshot may be the last one
 * an activity emits, and not promoted to top level, which would make the orphan visibly jump once its
 * container lands.
 */
function attachWidgets(state: FoldState): void {
  for (const step of state.steps) {
    const widgets = step.activityId ? state.widgetsByContainerId.get(step.activityId) : undefined
    if (widgets) {
      // Project `sequence` away: it is the fold's freshness key, not part of the render contract, and
      // leaving it on would make every consumer and assertion carry a field the card never reads.
      step.widgets = [...widgets.values()].map(({ id, payload }) => ({ id, payload }))
    }
  }
}

/**
 * Transform SSE events into activity step cards.
 *
 * Progress events fold into one step per activity, keyed on `activity_id` and falling back to the event
 * title for an emitter that sends none. Widget snapshots fold to `max(activity_sequence)` per `activity_id`
 * and render nested inside the activity their `container_id` names. Both kinds are folded in one pass in
 * receipt order, because a widget can only resolve against a container the pass has already seen.
 */
export function eventsToSteps({
  events,
  hasCompletedEvent,
  showConfirmation,
}: {
  events: AnalysisStreamEvent[]
  hasCompletedEvent: boolean
  showConfirmation: boolean
}): ActivityStep[] {
  const state: FoldState = {
    steps: [],
    stepsByActivityId: new Map(),
    stepsByTitle: new Map(),
    widgetsByContainerId: new Map(),
  }

  for (const event of events) {
    match(event)
      .with({ type: 'data-progress-finding', content: { status: 'in_progress' } }, ({ content }) => {
        applyInProgressEvent(state, content)
      })
      .with({ type: 'data-progress-finding', content: { status: 'completed' } }, ({ content }) => {
        applyCompletedEvent(state, content)
      })
      .with({ type: 'data-widget-snapshot' }, ({ content }) => {
        applyWidgetSnapshot(state, content)
      })
      .otherwise(() => undefined)
  }

  attachWidgets(state)

  if (hasCompletedEvent || showConfirmation) {
    for (const step of state.steps) {
      step.status = 'completed'
    }
  }

  return state.steps
}
